// Components
import {
  Stack,
  Text,
  Card,
  Group,
  SegmentedControl,
  Radio,
  Button,
  Badge,
  Divider,
  Alert,
  Progress,
  Collapse,
  ActionIcon,
  TextInput,
  Box,
} from "@mantine/core";
import {
  IconCheck,
  IconAlertCircle,
  IconBrain,
  IconChevronDown,
  IconChevronUp,
  IconX,
} from "@tabler/icons-react";

// Hooks
import { useState, useRef, useEffect } from "react";

// Utils
import {
  evaluateQuestionnaireCompletion,
  getEnhancedQuestionnaireConfig,
  filterScorableResponses,
} from "@benchmark/core/services";
import { calculateQuestionnaireScore } from "@benchmark/core/scoring";
import { isValidQuestionnaireScoring } from "@benchmark/core/constants";
import {
  getQuestionnaireDefinition,
  QUESTIONNAIRE_URLS,
  WSAS_WORK_NOT_APPLICABLE_VALUE,
} from "@benchmark/core/constants";
import {
  collectGatedOutLinkIds,
  computeGatedHiddenLinkIds,
} from "@/utils/questionnaireGating";

// Components
import { FloatingLabelInput } from "@benchmark/ui/components/atomic";

// Types
import type { QuestionnaireResponse } from "@benchmark/core/types";
import type { QuestionnaireItem as FHIRQuestionnaireItem } from "@medplum/fhirtypes";

/**
 * Extended FHIR QuestionnaireItem with custom UI properties.
 * @agentic @benchmark/core
 */
type QuestionItem = FHIRQuestionnaireItem & {
  /** Custom instruction text for additional guidance */
  instruction?: string;
  /** When true, render without numeric prefix (e.g., screening/gate items) */
  suppressNumbering?: boolean;
  /** Gate logic: when this item is answered with value 0/1, hide linked items */
  gateFor?: string[];
  /** Optional grouping label to render section headers (e.g., Part A / Part B) */
  section?: string;
  /** When false, this item's response is NOT included in score calculation (default: true) */
  scorable?: boolean;
  /** Custom answer options with numeric values */
  answerOptions?: Array<{
    value: number;
    code: string;
    display: string;
  }>;
  /** Simplified answer options */
  options?: Array<{
    value: number;
    display: string;
  }>;
  /** Placeholder text for string inputs */
  placeholder?: string;
  /** Max characters for string inputs */
  maxLength?: number;
  /** Nested items for groups */
  item?: QuestionItem[];
};

/**
 * Props for QuestionnaireRenderer component.
 * @agentic @benchmark/core
 */
type QuestionnaireRendererProps = {
  /** Questionnaire URL to render */
  questionnaireUrl: string;
  /** Current responses for this questionnaire */
  responses: QuestionnaireResponse;
  /** Callback when responses change */
  onChange: (responses: QuestionnaireResponse) => void;
  /** Whether the questionnaire is disabled */
  disabled?: boolean;
  /** Whether to show the calculated score */
  showScore?: boolean;
  /** Whether to show validation errors for incomplete questionnaires */
  showValidationErrors?: boolean;
  /**
   * Whether to show the instrument's on-page attribution / credit line (D2),
   * when the registry provides one. Used by the adaptive screener rail.
   */
  showAttribution?: boolean;
};

// TODO(#991): CRITICAL - Improve type safety and maintainability, including moving questions to the medplum package
//
// CURRENT ISSUE: The QuestionItem interface is not properly typed from FHIR;
// We should AT LEAST use Extends and Omits to make it more type safe

/**
 * UI configuration for questionnaires (icon, color).
 * @agentic @benchmark/core
 */
const UI_QUESTIONNAIRE_CONFIG = {
  icon: <IconBrain size={20} />,
  color: "purple" as const,
};

/**
 * Calculates score for completed questionnaire using consolidated utility.
 * @agentic @benchmark/core
 */
function calculateScore(
  responses: QuestionnaireResponse,
  questionnaireUrl: string,
) {
  const config = getEnhancedQuestionnaireConfig(questionnaireUrl);
  if (!config || !config.scoring) return null;

  // Strongly typed validation using type guard
  if (!isValidQuestionnaireScoring(config.scoring)) {
    console.error("Questionnaire scoring configuration failed validation", {
      questionnaireUrl,
      config,
    });
    return null;
  }

  // Filter to valid, scorable responses (excludes items with scorable: false)
  const validResponses = responses.responses.filter(
    (r) => r.value !== undefined,
  );
  const scorableResponses = filterScorableResponses(
    validResponses,
    questionnaireUrl,
  );
  if (scorableResponses.length === 0) return null;

  const result = calculateQuestionnaireScore(scorableResponses, config.scoring);

  return {
    ...result,
    maxScore: config.scoring.maxScore || 0,
  };
}

/**
 * Flattens nested items for completion tracking recursively.
 * @agentic @benchmark/core
 */
function getAllRequiredItems(items: QuestionItem[]): QuestionItem[] {
  const result: QuestionItem[] = [];

  for (const item of items) {
    if (item.type === "group" && item.item) {
      result.push(...getAllRequiredItems(item.item));
    } else if (item.required !== false) {
      result.push(item);
    }
  }

  return result;
}

/**
 * Renders and collects responses to standardized questionnaires.
 * @agentic @benchmark/core
 */
export const QuestionnaireRenderer = ({
  questionnaireUrl,
  responses,
  onChange,
  disabled = false,
  showScore = false,
  showValidationErrors = false,
  showAttribution = false,
}: QuestionnaireRendererProps) => {
  const config = getEnhancedQuestionnaireConfig(questionnaireUrl);
  const attribution = showAttribution
    ? getQuestionnaireDefinition(questionnaireUrl)?.attribution
    : undefined;
  const [questionsExpanded, setQuestionsExpanded] = useState(true);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Handle smooth transition when questionnaire is completed (only once)
  useEffect(() => {
    // Do not auto-collapse while there are optional questions unanswered
    // Apply simple gating: if any item has gateFor and current response value === 0, hide gated items
    const responseMap = new Map(
      responses.responses.map((r) => [r.linkId, r.value]),
    );
    const allQuestions = (config?.questions || []) as QuestionItem[];
    const gatedHidden = new Set<string>();
    allQuestions.forEach((q: QuestionItem) => {
      if (q.gateFor && responseMap.get(q.linkId) === 0) {
        q.gateFor.forEach((id: string) => gatedHidden.add(id));
      }
    });
    const visibleQuestions = allQuestions.filter(
      (q: QuestionItem) => !gatedHidden.has(q.linkId),
    );
    // Count only optional questions for auto-collapse guard
    const optionalQuestions = visibleQuestions.filter(
      (q: QuestionItem) => q.required === false && q.type !== "group",
    );
    const answeredOptionalCount = optionalQuestions.filter((q: QuestionItem) =>
      responses.responses.some(
        (r) =>
          r.linkId === q.linkId &&
          (r.value !== undefined || r.valueString !== undefined),
      ),
    ).length;

    const hasUnansweredOptional =
      optionalQuestions.length > 0 &&
      answeredOptionalCount < optionalQuestions.length;

    if (
      responses.completed &&
      questionsExpanded &&
      !hasAutoCollapsed &&
      !hasUnansweredOptional
    ) {
      // Collapse questions after a brief delay to show completion
      const collapseTimer = setTimeout(() => {
        setQuestionsExpanded(false);
        setHasAutoCollapsed(true);
      }, 500);

      // Scroll to the result card after collapse animation
      const scrollTimer = setTimeout(() => {
        if (cardRef.current) {
          // Get the card's position relative to the viewport
          const cardRect = cardRef.current.getBoundingClientRect();
          const currentScrollTop =
            window.pageYOffset || document.documentElement.scrollTop;

          // Calculate the desired scroll position with padding above the card
          // We want to show the card header with some breathing room (80px padding)
          const desiredScrollTop = currentScrollTop + cardRect.top - 80;

          // Smooth scroll to the calculated position
          window.scrollTo({
            top: Math.max(0, desiredScrollTop), // Ensure we don't scroll above the top
            behavior: "smooth",
          });
        }
      }, 1200);

      return () => {
        clearTimeout(collapseTimer);
        clearTimeout(scrollTimer);
      };
    }
  }, [
    responses.completed,
    questionsExpanded,
    hasAutoCollapsed,
    config,
    responses.responses,
  ]);

  if (!config) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Unknown questionnaire: {questionnaireUrl}
      </Alert>
    );
  }

  if ("notImplemented" in config && config.notImplemented) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Coming Soon"
        color="yellow"
      >
        <div>
          <strong>{config.name}</strong> questionnaire is available for
          selection but the full interactive form is not yet implemented.
        </div>
      </Alert>
    );
  }

  // Completion counts only the questions currently visible: a gated-out subtree
  // (a `gateFor` parent answered 0) must not keep the instrument incomplete —
  // e.g. AUDIT-C answered "Never" is complete at 0/12 without items 2–3.
  const deriveCompleted = (
    nextResponses: typeof responses.responses,
  ): boolean => {
    const completion = evaluateQuestionnaireCompletion(
      questionnaireUrl,
      nextResponses,
      // Any answer edit revokes a prior manual completion declaration. This is
      // essential for all-optional instruments (Safety Plan): clearing the last
      // answer must require an explicit re-mark, not silently remain complete.
      { declaredComplete: false },
    );
    return completion.supported && completion.complete;
  };

  const handleResponseChange = (
    linkId: string,
    value: number,
    coding: { code: string; display: string },
  ) => {
    let newResponses = [...responses.responses];
    const existingIndex = newResponses.findIndex((r) => r.linkId === linkId);

    if (existingIndex >= 0) {
      newResponses[existingIndex] = { linkId, value, coding };
    } else {
      newResponses.push({ linkId, value, coding });
    }

    // When this answer gates out linked items (value 0 with gateFor), drop any
    // stale answers for the now-hidden subtree so gated-out responses are never
    // counted downstream — e.g. a C-SSRS parent answered "No" must not leave
    // positive sub-item answers behind that read as a positive screen.
    const gatedOut = collectGatedOutLinkIds(
      (config.questions || []) as QuestionItem[],
      linkId,
      value,
    );
    if (gatedOut.size > 0) {
      newResponses = newResponses.filter((r) => !gatedOut.has(r.linkId));
    }

    const completed = deriveCompleted(newResponses);

    onChange({
      responses: newResponses,
      completed,
    });
  };

  const handleStringResponseChange = (linkId: string, value: string) => {
    const newResponses = [...responses.responses];
    const existingIndex = newResponses.findIndex((r) => r.linkId === linkId);

    if (existingIndex >= 0) {
      if (value === "") {
        // Remove response if empty
        newResponses.splice(existingIndex, 1);
      } else {
        newResponses[existingIndex] = { linkId, valueString: value };
      }
    } else if (value !== "") {
      newResponses.push({ linkId, valueString: value });
    }

    const completed = deriveCompleted(newResponses);

    onChange({
      responses: newResponses,
      completed,
    });
  };

  const handleMarkComplete = () => {
    onChange({
      ...responses,
      completed: true,
    });
  };

  function handleClearResponse(linkId: string) {
    const newResponses = responses.responses.filter((r) => r.linkId !== linkId);

    const completed = deriveCompleted(newResponses);

    onChange({
      responses: newResponses,
      completed,
    });
  }

  type NormalizedAnswerOption = {
    value: number;
    code: string;
    display: string;
  };

  const getAnswerOptions = (question: QuestionItem): NormalizedAnswerOption[] =>
    question.answerOptions ||
    question.options?.map((opt) => ({
      value: opt.value,
      code: opt.value.toString(),
      display: opt.display,
    })) ||
    [];

  const handleChoiceChange = (
    question: QuestionItem,
    numValue: number,
    answerOptions: NormalizedAnswerOption[],
  ) => {
    const option = answerOptions.find((opt) => opt.value === numValue);
    if (option) {
      handleResponseChange(question.linkId, numValue, {
        code: option.code,
        display: option.display,
      });
    }
  };

  const renderWsasScaleInput = (
    question: QuestionItem,
    currentResponse: (typeof responses.responses)[number] | undefined,
    hasError: boolean = false,
  ) => {
    const answerOptions = getAnswerOptions(question);
    const naOption = answerOptions.find(
      (option) => option.value === WSAS_WORK_NOT_APPLICABLE_VALUE,
    );
    const scaleOptions = answerOptions.filter(
      (option) => option.value >= 0 && option.value <= 8,
    );
    const selectedValue = currentResponse?.value;
    const isNaSelected = selectedValue === WSAS_WORK_NOT_APPLICABLE_VALUE;
    const lowAnchor =
      scaleOptions.find((option) => option.value === 0)?.display ??
      "Not at all impaired";
    const highAnchor =
      scaleOptions.find((option) => option.value === 8)?.display ??
      "Very severely impaired";

    return (
      <Stack gap="xs" style={{ flex: 1, width: "100%" }}>
        {naOption ? (
          <Radio
            checked={isNaSelected}
            onChange={() => {
              handleChoiceChange(question, naOption.value, answerOptions);
            }}
            label={naOption.display}
            disabled={disabled}
          />
        ) : null}
        <SegmentedControl
          style={{ flex: 1 }}
          value={
            isNaSelected || selectedValue === undefined
              ? ""
              : selectedValue.toString()
          }
          onChange={(value) => {
            handleChoiceChange(question, parseInt(value, 10), answerOptions);
          }}
          data={scaleOptions.map((option) => ({
            label: option.value.toString(),
            value: option.value.toString(),
          }))}
          disabled={disabled}
          color={hasError ? "red" : UI_QUESTIONNAIRE_CONFIG.color}
          size="sm"
        />
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" c="dimmed">
            {lowAnchor}
          </Text>
          <Text size="xs" c="dimmed" ta="right">
            {highAnchor}
          </Text>
        </Group>
      </Stack>
    );
  };

  const renderChoiceInput = (
    question: QuestionItem,
    currentResponse: (typeof responses.responses)[number] | undefined,
    hasError: boolean = false,
  ) => {
    const answerOptions = getAnswerOptions(question);
    const selectedValue = currentResponse?.value?.toString() ?? "";

    if (config.url === QUESTIONNAIRE_URLS.WSAS) {
      return renderWsasScaleInput(question, currentResponse, hasError);
    }

    return (
      <SegmentedControl
        style={{ flex: 1 }}
        value={selectedValue}
        onChange={(value) => {
          handleChoiceChange(question, parseInt(value, 10), answerOptions);
        }}
        data={answerOptions.map((option) => ({
          label: option.display,
          value: option.value.toString(),
        }))}
        disabled={disabled}
        color={hasError ? "red" : UI_QUESTIONNAIRE_CONFIG.color}
        size="sm"
      />
    );
  };

  // Build gated visibility for main render
  const renderResponseMap = new Map(
    responses.responses.map((r) => [r.linkId, r.value]),
  );
  // `config.questions` can legitimately be null for placeholder/coming-soon
  // questionnaire configs; the per-handler paths above already guard with
  // `?? []`, the main render path needs the same defensive default.
  const allRenderQuestions = (config.questions ?? []) as QuestionItem[];
  const renderGatedHidden = computeGatedHiddenLinkIds(
    allRenderQuestions,
    renderResponseMap,
  );
  const visibleRenderQuestions = allRenderQuestions.filter(
    (q: QuestionItem) => !renderGatedHidden.has(q.linkId),
  );
  const requiredQuestions = getAllRequiredItems(visibleRenderQuestions);
  const optionalQuestions = visibleRenderQuestions.filter(
    (q: QuestionItem) => q.required === false && q.type !== "group",
  );
  // Optional (required: false) items are rendered exclusively in the dedicated
  // "Optional" section below, so exclude them from the main numbered list —
  // otherwise a required-false item (e.g. the PHQ-9 functional-impairment
  // question) renders twice. Groups still flow through the main list.
  const mainRenderQuestions = visibleRenderQuestions.filter(
    (q: QuestionItem) => !(q.required === false && q.type !== "group"),
  );
  const answeredRequiredCount = requiredQuestions.filter((q: QuestionItem) =>
    responses.responses.some(
      (r) =>
        r.linkId === q.linkId &&
        (r.value !== undefined || r.valueString !== undefined),
    ),
  ).length;
  const totalRequiredQuestions = requiredQuestions.length;
  const progressPercent =
    totalRequiredQuestions > 0
      ? (answeredRequiredCount / totalRequiredQuestions) * 100
      : 0;
  // Hide score display for non-scored questionnaires
  const completion = evaluateQuestionnaireCompletion(
    questionnaireUrl,
    responses.responses,
    { declaredComplete: responses.completed },
  );
  const scoreData =
    showScore &&
    completion.supported &&
    completion.complete &&
    config.scoring &&
    config.scoring.linkIds.length > 0
      ? calculateScore(responses, questionnaireUrl)
      : null;

  // Categorical instruments (drug-use, PGI-S, PGI-C — D7) report the chosen
  // category as a label, never a number out of a total.
  const isCategorical =
    getQuestionnaireDefinition(questionnaireUrl)?.scoringMethod === "category";

  // Determine whether to render an item-level instruction without duplicating the global one
  function shouldShowItemInstruction(itemInstruction?: string): boolean {
    if (!itemInstruction) return false;
    const globalInstruction = (config?.instructions ?? "").trim().toLowerCase();
    const localInstruction = itemInstruction.trim().toLowerCase();
    return !globalInstruction || globalInstruction !== localInstruction;
  }

  // Helper to render a compact input (no Card wrapper, uses floating label)
  function renderCompactInput(item: QuestionItem): React.ReactNode {
    const currentResponse = responses.responses.find(
      (r) => r.linkId === item.linkId,
    );
    const isUnanswered =
      currentResponse?.value === undefined &&
      currentResponse?.valueString === undefined;
    const hasError =
      showValidationErrors &&
      !responses.completed &&
      isUnanswered &&
      item.required;

    if (item.type === "string") {
      return (
        <Box key={item.linkId} style={{ flex: 1 }}>
          <FloatingLabelInput
            label={item.text || ""}
            placeholder={item.placeholder}
            value={currentResponse?.valueString || ""}
            onChange={(value: string) =>
              handleStringResponseChange(item.linkId, value)
            }
            disabled={disabled}
            error={hasError}
            maxLength={item.maxLength}
          />
        </Box>
      );
    }

    // For choice items in compact mode, still render with Card for now
    // This maintains existing behavior for choice questions
    return renderItem(item, 0, { suppressNumbering: true, compact: false });
  }

  // Helper to render a single item (choice or string)
  function renderItem(
    question: QuestionItem,
    displayIndex: number,
    options?: { suppressNumbering?: boolean; compact?: boolean },
  ): React.ReactNode {
    const currentResponse = responses.responses.find(
      (r) => r.linkId === question.linkId,
    );
    const isUnanswered =
      currentResponse?.value === undefined &&
      currentResponse?.valueString === undefined;
    const hasError =
      showValidationErrors &&
      !responses.completed &&
      isUnanswered &&
      question.required;
    const suppressNumbering =
      options?.suppressNumbering || question.suppressNumbering;
    const compact = options?.compact ?? false;

    if (question.type === "string") {
      // Compact mode: no Card wrapper, minimal styling
      if (compact) {
        return (
          <Box key={question.linkId} mb="xs">
            <Text size="sm" fw={500} mb="xs" c={hasError ? "red" : undefined}>
              {suppressNumbering
                ? question.text
                : `${displayIndex}. ${question.text}`}
            </Text>
            <TextInput
              placeholder={question.placeholder || question.text}
              value={currentResponse?.valueString || ""}
              onChange={(e) =>
                handleStringResponseChange(question.linkId, e.target.value)
              }
              disabled={disabled}
              maxLength={question.maxLength}
              error={hasError}
            />
          </Box>
        );
      }

      // Full Card mode (default for top-level items)
      return (
        <Card
          key={question.linkId}
          p="sm"
          style={{
            border: hasError
              ? "1px solid var(--mantine-color-red-6)"
              : undefined,
            backgroundColor: hasError
              ? "var(--mantine-color-red-0)"
              : undefined,
          }}
        >
          <Stack gap="xs">
            <Text size="sm" fw={500} c={hasError ? "red" : undefined}>
              {suppressNumbering
                ? question.text
                : `${displayIndex}. ${question.text}`}
            </Text>

            {shouldShowItemInstruction(question.instruction) && (
              <Text size="xs" c="dimmed">
                {question.instruction}
              </Text>
            )}

            <TextInput
              placeholder={question.placeholder}
              value={currentResponse?.valueString || ""}
              onChange={(e) =>
                handleStringResponseChange(question.linkId, e.target.value)
              }
              disabled={disabled}
              maxLength={question.maxLength}
              error={hasError}
            />
          </Stack>
        </Card>
      );
    }

    // Existing choice rendering logic
    return (
      <Card
        key={question.linkId}
        p="sm"
        style={{
          border: hasError ? "1px solid var(--mantine-color-red-6)" : undefined,
          backgroundColor: hasError ? "var(--mantine-color-red-0)" : undefined,
        }}
      >
        <Stack gap="xs">
          <Text size="sm" fw={500} c={hasError ? "red" : undefined}>
            {suppressNumbering
              ? question.text
              : `${displayIndex}. ${question.text}`}
          </Text>

          {shouldShowItemInstruction(question.instruction) && (
            <Text size="xs" c="dimmed">
              {question.instruction}
            </Text>
          )}

          <Group gap="xs" align="center" style={{ width: "100%" }}>
            {renderChoiceInput(question, currentResponse, hasError)}
          </Group>
        </Stack>
      </Card>
    );
  }

  // Helper to render a group of items
  function renderGroup(group: QuestionItem, isNested = false): React.ReactNode {
    // Check if this is a contact-style nested group (2-3 string children)
    const isContactStyle =
      isNested &&
      group.item &&
      group.item.every((item) => item.type === "string") &&
      group.item.length >= 2 &&
      group.item.length <= 3;

    // For contact-style groups, render horizontally
    if (isContactStyle && group.item) {
      return (
        <Box key={group.linkId} mb="md">
          <Text size="sm" fw={500} mb="xs">
            {group.text}
          </Text>
          <Group grow gap="sm" align="flex-start">
            {group.item.map((item) => renderCompactInput(item))}
          </Group>
        </Box>
      );
    }

    // For step-level groups (top-level), use Card with border
    if (!isNested) {
      return (
        <Card key={group.linkId} withBorder p="md" mb="md">
          <Stack gap="sm">
            <Text fw={600} size="md">
              {group.text}
            </Text>
            {group.instruction && (
              <Text size="sm" c="dimmed">
                {group.instruction}
              </Text>
            )}
            <Stack gap="xs">
              {group.item?.map((item) => {
                if (item.type === "group") {
                  return renderGroup(item, true);
                }
                // For items in groups, suppress numbering and use compact mode
                return renderItem(item, 0, {
                  suppressNumbering: true,
                  compact: true,
                });
              })}
            </Stack>
          </Stack>
        </Card>
      );
    }

    // For other nested groups, use lighter styling
    return (
      <Box
        key={group.linkId}
        mb="sm"
        pl="md"
        style={{ borderLeft: "2px solid var(--mantine-color-gray-3)" }}
      >
        <Text size="sm" fw={500} mb="xs">
          {group.text}
        </Text>
        {group.instruction && (
          <Text size="xs" c="dimmed" mb="xs">
            {group.instruction}
          </Text>
        )}
        <Stack gap="xs">
          {group.item?.map((item) => {
            if (item.type === "group") {
              return renderGroup(item, true);
            }
            // For items in nested groups, suppress numbering and use compact mode
            return renderItem(item, 0, {
              suppressNumbering: true,
              compact: true,
            });
          })}
        </Stack>
      </Box>
    );
  }

  // Display numbering counter for required questions (excludes suppressed-numbering items)
  let displayIndex = 0;

  return (
    <Card withBorder ref={cardRef}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <div
              style={{
                color: `var(--mantine-color-${UI_QUESTIONNAIRE_CONFIG.color}-6)`,
              }}
            >
              {UI_QUESTIONNAIRE_CONFIG.icon}
            </div>
            <div>
              <Text fw={500}>{config.name}</Text>
              <Text size="sm" c="dimmed">
                {config.title}
              </Text>
            </div>
          </Group>

          {responses.completed && (
            <Badge
              color="green"
              variant="light"
              leftSection={<IconCheck size={12} />}
            >
              Complete
            </Badge>
          )}
        </Group>

        {/* Progress */}
        {!responses.completed && (
          <div>
            <Group justify="space-between" mb="xs">
              <Text size="sm">Progress</Text>
              <Text size="sm" c="dimmed">
                {answeredRequiredCount} of {totalRequiredQuestions} answered
              </Text>
            </Group>
            <Progress
              value={progressPercent}
              color={UI_QUESTIONNAIRE_CONFIG.color}
              size="sm"
            />
          </div>
        )}

        {/* Global clinical instructions */}
        {config.instructions && (
          <Text size="sm" c="dimmed">
            {config.instructions}
          </Text>
        )}

        {/* Score / result display. Categorical instruments (D7) show the chosen
            category as a label — never a number out of a total. */}
        {scoreData && (
          <>
            <Divider />
            <Group justify="space-between" align="center">
              <div>
                <Text size="sm" fw={500}>
                  {isCategorical ? "Result" : "Score"}
                </Text>
                {!isCategorical && (
                  <Text size="xs" c="dimmed">
                    {scoreData.interpretation}
                  </Text>
                )}
              </div>
              <Badge color={scoreData.riskColor} variant="filled" size="lg">
                {isCategorical
                  ? scoreData.interpretation
                  : `${scoreData.score} / ${scoreData.maxScore}`}
              </Badge>
            </Group>
          </>
        )}

        {/* Questions Section */}
        <>
          <Divider />

          {/* Questions Header with Toggle (when completed) */}
          {responses.completed && (
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                Questions
              </Text>
              <ActionIcon
                variant="subtle"
                onClick={() => setQuestionsExpanded(!questionsExpanded)}
              >
                {questionsExpanded ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </ActionIcon>
            </Group>
          )}

          <Collapse in={questionsExpanded || !responses.completed}>
            <Stack gap={0}>
              {/* Render questions (groups or individual required items).
                  Optional items are rendered once in the "Optional" section. */}
              {mainRenderQuestions.map((question: QuestionItem) => {
                if (question.type === "group") {
                  return renderGroup(question, false);
                }
                return renderItem(question, ++displayIndex);
              })}

              {/* Optional follow-up (not scored) */}
              {optionalQuestions.length > 0 && (
                <>
                  <Divider />
                  <Text size="sm" fw={500} c="dimmed">
                    Optional
                  </Text>
                  {optionalQuestions.map((question: QuestionItem) => {
                    const currentResponse = responses.responses.find(
                      (r) => r.linkId === question.linkId,
                    );
                    const hasValue =
                      currentResponse?.value !== undefined ||
                      currentResponse?.valueString !== undefined;

                    if (question.type === "string") {
                      return (
                        <Card key={question.linkId} p="sm">
                          <Stack gap="xs">
                            <Text size="sm" fw={500}>
                              {question.text}
                            </Text>

                            {shouldShowItemInstruction(
                              question.instruction,
                            ) && (
                              <Text size="xs" c="dimmed">
                                {question.instruction}
                              </Text>
                            )}

                            <Group
                              gap="xs"
                              align="center"
                              style={{ width: "100%" }}
                            >
                              <TextInput
                                style={{ flex: 1 }}
                                placeholder={question.placeholder}
                                value={currentResponse?.valueString || ""}
                                onChange={(e) =>
                                  handleStringResponseChange(
                                    question.linkId,
                                    e.target.value,
                                  )
                                }
                                disabled={disabled}
                                maxLength={question.maxLength}
                              />
                              {hasValue && !disabled && (
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={() =>
                                    handleClearResponse(question.linkId)
                                  }
                                  title="Clear"
                                >
                                  <IconX size={14} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Stack>
                        </Card>
                      );
                    }

                    return (
                      <Card key={question.linkId} p="sm">
                        <Stack gap="xs">
                          <Text size="sm" fw={500}>
                            {question.text}
                          </Text>

                          {shouldShowItemInstruction(question.instruction) && (
                            <Text size="xs" c="dimmed">
                              {question.instruction}
                            </Text>
                          )}

                          <Group
                            gap="xs"
                            align="center"
                            style={{ width: "100%" }}
                          >
                            {renderChoiceInput(
                              question,
                              currentResponse,
                              false,
                            )}
                            {hasValue && !disabled && (
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={() =>
                                  handleClearResponse(question.linkId)
                                }
                                title="Clear"
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            )}
                          </Group>
                        </Stack>
                      </Card>
                    );
                  })}
                </>
              )}
            </Stack>
          </Collapse>
        </>

        {/* Actions */}
        {!disabled &&
          answeredRequiredCount === totalRequiredQuestions &&
          !responses.completed && (
            <>
              <Divider />
              <Group justify="center">
                <Button
                  onClick={handleMarkComplete}
                  color={UI_QUESTIONNAIRE_CONFIG.color}
                  leftSection={<IconCheck size={16} />}
                >
                  Mark Assessment Complete
                </Button>
              </Group>
            </>
          )}

        {/* On-page attribution / credit line (D2). */}
        {attribution && (
          <>
            <Divider />
            <Text size="xs" c="dimmed" data-testid="questionnaire-attribution">
              {attribution}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
};
